import App from "./App";
import { createRoot } from "react-dom/client";
import "./i18n/config";
import "./index.css";

const ensurePromiseWithResolvers = () => {
  const promiseCtor = Promise as PromiseConstructor & {
    withResolvers?: <T>() => {
      promise: Promise<T>;
      resolve: (value: T | PromiseLike<T>) => void;
      reject: (reason?: unknown) => void;
    };
  };
  if (typeof promiseCtor.withResolvers === "function") return;

  Object.defineProperty(Promise, "withResolvers", {
    configurable: true,
    writable: true,
    value: function withResolvers<T>() {
      let resolve!: (value: T | PromiseLike<T>) => void;
      let reject!: (reason?: unknown) => void;
      const promise = new Promise<T>((res, rej) => {
        resolve = res;
        reject = rej;
      });
      return { promise, resolve, reject };
    },
  });
};

ensurePromiseWithResolvers();

// Declare renderEventFired on Window interface for TypeScript
declare global {
  interface Window {
    renderEventFired?: boolean;
  }
}

// ─── PWA Guard: Prevent SW in iframe/preview contexts ───────────
const isInIframe = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

const isPreviewHost =
  window.location.hostname.includes("id-preview--") ||
  window.location.hostname.includes("lovableproject.com");

if (isPreviewHost || isInIframe) {
  navigator.serviceWorker?.getRegistrations().then((registrations) => {
    registrations.forEach((r) => r.unregister());
  });
}

// ─── Password recovery redirect ─────────────────────────────────
(() => {
  if (typeof window === "undefined") return;
  // Branded email links must verify their one-time hash before recovery is
  // trusted. Do not rewrite this route or discard its nested storefront next.
  if (window.location.pathname === "/auth/confirm") return;
  const hash = window.location.hash || "";
  const search = window.location.search || "";
  const params = new URLSearchParams(`${search.replace(/^\?/, "")}&${hash.replace(/^#/, "")}`);
  const isRecovery =
    params.get("type") === "recovery" ||
    (params.has("access_token") && Array.from(params.values()).includes("recovery"));
  if (isRecovery) {
    sessionStorage.setItem("ec_password_recovery_active", "true");
    sessionStorage.removeItem("ec_return_path");
  }
  if (isRecovery && window.location.pathname !== "/reset-password" && !/\/auth\/reset$/.test(window.location.pathname)) {
    const target = `/reset-password${search}${hash}`;
    window.history.replaceState(null, "", target);
  }
})();

// ─── Render Root ────────────────────────────────────────────────
// Wait for the active language pack before first render so the UI
// never flashes raw translation keys (locales are lazy-loaded).
const bootstrap = async () => {
  let hydrateRest: (() => Promise<void>) | null = null;
  let fullyLoaded: Promise<unknown> = Promise.resolve();
  try {
    // Only the critical half of the active locale blocks first paint.
    const mod = await import("./i18n/config");
    await mod.i18nReady;
    hydrateRest = () => mod.hydrateLocaleRest();
    fullyLoaded = mod.i18nFullyLoaded;
  } catch {
    // Render anyway with fallback keys rather than blocking the app.
  }

  const container = document.getElementById("root");
  if (!container) return;
  const root = createRoot(container);
  root.render(<App />);

  // The remaining translations are merged into the same i18next store
  // right after the first paint, so deeper pages never show raw keys.
  void hydrateRest?.();

  // Signal to prerender that the page is ready — only once every
  // translation is in place, so crawled markup stays identical.
  if (typeof window !== "undefined") {
    void fullyLoaded
      .catch(() => {})
      .then(() => {
        window.dispatchEvent(new Event("render-event"));
        window.renderEventFired = true;
      });
  }

  const runAfterFirstPaint = (task: () => void) => {
    window.requestAnimationFrame(() => window.setTimeout(task, 0));
  };

  runAfterFirstPaint(() => {
    // Initialize non-critical telemetry after React has replaced the initial paint.
    import("./lib/sentry").then(m => m.initSentry()).catch(() => {});
    import("./lib/reportWebVitals").then(m => m.reportWebVitals()).catch(() => {});
    import("./components/ai/malakCounterPersistence")
      .then((m) => m.installMalakCounterPersistence())
      .catch(() => {});
    import("./lib/nativeShell").then((m) => m.initNativeShell()).catch(() => {});
  });
};

void bootstrap();
